import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  createUser,
  mergeUserPhotoIntoScope,
  updateProfile,
  uploadUserDocument,
  uploadUserPhoto,
} from '@/api/users'
import { listCompanies } from '@/api/companies'
import { Button } from '@/components/ui/Button'
import { PillToggle } from '@/components/ui/PillToggle'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { PasswordDialog } from '@/components/users/PasswordDialog'
import type { UserRole } from '@/types/enums'
import { formatPhoneInput } from '@/utils/phone'

export function UserDrawer({
  open,
  onClose,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [phone, setPhone] = useState('+7 ')
  const [email, setEmail] = useState('')
  const [companyId, setCompanyId] = useState<string>('')
  const [role, setRole] = useState<UserRole>('manager')
  const [hasPd, setHasPd] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [pwDialog, setPwDialog] = useState<string | null>(null)
  const [createdUserId, setCreatedUserId] = useState<string | null>(null)
  const [photoFile, setPhotoFile] = useState<File | null>(null)

  // Расширенные поля по ТЗ — заполняются по роли.
  const [registrationAddress, setRegistrationAddress] = useState('')
  const [passportSeries, setPassportSeries] = useState('')
  const [passportNumber, setPassportNumber] = useState('')
  const [passportIssuedBy, setPassportIssuedBy] = useState('')
  const [passportIssuedAt, setPassportIssuedAt] = useState('')
  const [poaNumber, setPoaNumber] = useState('')
  const [poaExpiresAt, setPoaExpiresAt] = useState('')
  const [passportMainFile, setPassportMainFile] = useState<File | null>(null)
  const [passportRegistrationFile, setPassportRegistrationFile] = useState<File | null>(null)
  const [poaFile, setPoaFile] = useState<File | null>(null)

  const fullName = [lastName.trim(), firstName.trim(), middleName.trim()]
    .filter(Boolean)
    .join(' ')
    .trim()

  const photoPreviewUrl = useMemo(
    () => (photoFile ? URL.createObjectURL(photoFile) : null),
    [photoFile],
  )
  useEffect(
    () => () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl)
    },
    [photoPreviewUrl],
  )

  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'active'],
    queryFn: () => listCompanies({ pageSize: 200 }),
  })
  const companies = companiesData?.items ?? []

  const selectedCompany = companies.find((c) => c.id === companyId)
  const roleOptions: { value: UserRole; label: string }[] =
    !selectedCompany
      ? [
          // Платформа «Стриж» (без компании): только сотрудники
          { value: 'dispatcher', label: t('role.dispatcher') },
        ]
      : selectedCompany.type === 'contractor'
        ? [
            { value: 'inspector', label: t('role.inspector') },
            { value: 'contractor_supervisor', label: t('role.contractorSupervisor') },
          ]
        : [
            { value: 'manager', label: t('role.manager') },
            { value: 'customer_supervisor', label: t('role.customerSupervisor') },
            // Кабинет партнёра: работает, если компания привязана к воронке
            // (lead_pipelines.partner_company_id).
            { value: 'partner_operator', label: t('role.partnerOperator') },
            { value: 'partner_viewer', label: t('role.partnerViewer') },
          ]

  if (!open) return null

  async function submit() {
    setError('')
    if (!lastName.trim()) { setError(t('users.validation.lastName')); return }
    if (!firstName.trim()) { setError(t('users.validation.firstName')); return }
    if (role === 'inspector' && !middleName.trim()) {
      setError(t('users.validation.middleName'))
      return
    }
    if (phone.replace(/\D/g, '').length < 11) {
      setError(t('users.validation.phone'))
      return
    }
    // По ТЗ компания обязательна для всех, кроме dispatcher (платформа Стриж).
    if (role !== 'dispatcher' && !companyId) {
      setError(t('users.validation.company'))
      return
    }
    if ((role === 'inspector' || role === 'manager') && !registrationAddress.trim()) {
      setError(
        role === 'inspector'
          ? t('users.validation.registrationAddress')
          : t('users.validation.locationAddress'),
      )
      return
    }
    if (role === 'inspector') {
      if (!passportSeries.trim() || !passportNumber.trim()) {
        setError(t('users.validation.passportSeriesNumber')); return
      }
      if (!passportIssuedBy.trim()) { setError(t('users.validation.passportIssuedBy')); return }
      if (!passportIssuedAt) { setError(t('users.validation.passportIssuedAt')); return }
      if (!passportMainFile) { setError(t('users.validation.passportMainScan')); return }
      if (!passportRegistrationFile) {
        setError(t('users.validation.passportRegistrationScan')); return
      }
    }
    if (role === 'manager') {
      if (!poaNumber.trim()) { setError(t('users.validation.poaNumber')); return }
      if (!poaExpiresAt) { setError(t('users.validation.poaExpiresAt')); return }
      if (!poaFile) { setError(t('users.validation.poaScan')); return }
    }

    setSaving(true)
    try {
      const { profile, password } = await createUser({
        full_name: fullName,
        phone,
        email: email || null,
        company_id: companyId || null,
        role,
        has_pd_access: hasPd,
      })

      // Дозаполняем расширенные поля и загружаем документы — отдельным `updateProfile`,
      // потому что `createUser` принимает минимальный набор (под существующий RPC).
      const extras: Record<string, unknown> = {
        last_name: lastName.trim() || null,
        first_name: firstName.trim() || null,
        middle_name: middleName.trim() || null,
        registration_address: registrationAddress.trim() || null,
      }
      if (role === 'inspector') {
        extras.passport_series = passportSeries.trim() || null
        extras.passport_number = passportNumber.trim() || null
        extras.passport_issued_by = passportIssuedBy.trim() || null
        extras.passport_issued_at = passportIssuedAt || null
      }
      if (role === 'manager') {
        extras.power_of_attorney_number = poaNumber.trim() || null
        extras.power_of_attorney_expires_at = poaExpiresAt || null
      }

      if (photoFile) {
        const avatarUrl = await uploadUserPhoto(profile.id, photoFile)
        extras.access_scope = mergeUserPhotoIntoScope(profile.access_scope, avatarUrl)
      }

      // Документы — приватный bucket user-documents.
      if (passportMainFile) {
        extras.passport_main_url = await uploadUserDocument(
          profile.id,
          'passport-main',
          passportMainFile,
        )
      }
      if (passportRegistrationFile) {
        extras.passport_registration_url = await uploadUserDocument(
          profile.id,
          'passport-registration',
          passportRegistrationFile,
        )
      }
      if (poaFile) {
        extras.power_of_attorney_url = await uploadUserDocument(
          profile.id,
          'power-of-attorney',
          poaFile,
        )
      }

      await updateProfile(profile.id, extras)

      setCreatedUserId(profile.id)
      setPwDialog(password)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.saveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex justify-end bg-[color:var(--stryzh-overlay-scrim-modal)]"
        role="presentation"
        onClick={onClose}
      >
        <aside
          className="flex h-full w-[var(--stryzh-layout-entity-drawer-width)] max-w-full flex-col overflow-hidden border-l border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] shadow-[var(--stryzh-shadow-glow-strong)]"
          role="dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
            <h2 className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
              {t('users.drawer.title')}
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              aria-label={t('users.drawer.close')}
              className="stryzh-btn--close-icon"
            >
              ✕
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
            <div className="space-y-3">
              <Input
                label={t('users.fields.lastName')} required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <Input
                label={t('users.fields.firstName')} required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <Input
                label={t('users.fields.middleName')}
                required={role === 'inspector'}
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
              />
              <Input
                label={t('users.fields.phone')}
                required
                value={phone}
                onChange={(e) =>
                  setPhone(formatPhoneInput(phone, e.target.value))
                }
              />
              <Input
                label={t('users.fields.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <label className="flex flex-col gap-1">
                <span className="stryzh-input-label">{t('users.fields.photo')}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                  className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
                />
                {photoPreviewUrl ? (
                  <img
                    src={photoPreviewUrl}
                    alt={t('users.fields.photoPreviewAlt')}
                    className="mt-2 h-20 w-20 rounded-full border border-[color:var(--stryzh-color-border-default)] object-cover"
                  />
                ) : null}
              </label>
              <label className="flex flex-col gap-1">
                <span className="stryzh-input-label">{t('users.fields.company')}</span>
                <Select
                  value={companyId}
                  onChange={(v) => {
                    setCompanyId(v)
                    setRole(v ? 'manager' : 'dispatcher')
                  }}
                  options={companies.map((c) => ({ value: c.id, label: c.name }))}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="stryzh-input-label">{t('users.fields.role')}</span>
                <Select
                  value={role}
                  onChange={(v) => setRole(v as UserRole)}
                  options={roleOptions}
                  disabled={!companyId}
                />
                {!companyId ? (
                  <span className="mt-1 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                    {t('users.drawer.selectCompanyFirst')}
                  </span>
                ) : null}
              </label>
              <PillToggle
                checked={hasPd}
                onChange={setHasPd}
                label={t('users.fields.pdAccess')}
              />

              {role === 'inspector' || role === 'manager' ? (
                <div className="space-y-3 border-t border-[color:var(--stryzh-color-border-default)] pt-3">
                  <Input
                    label={
                      role === 'inspector'
                        ? t('users.fields.registrationAddress')
                        : t('users.fields.locationAddress')
                    }
                    required
                    value={registrationAddress}
                    onChange={(e) => setRegistrationAddress(e.target.value)}
                  />
                </div>
              ) : null}

              {role === 'inspector' ? (
                <div className="space-y-3">
                  <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2">
                    <Input
                      label={t('users.fields.passportSeries')}
                      required
                      inputMode="numeric"
                      maxLength={4}
                      value={passportSeries}
                      onChange={(e) => setPassportSeries(e.target.value)}
                    />
                    <Input
                      label={t('users.fields.passportNumber')}
                      required
                      inputMode="numeric"
                      maxLength={6}
                      value={passportNumber}
                      onChange={(e) => setPassportNumber(e.target.value)}
                    />
                  </div>
                  <Input
                    label={t('users.fields.passportIssuedBy')}
                    required
                    value={passportIssuedBy}
                    onChange={(e) => setPassportIssuedBy(e.target.value)}
                  />
                  <Input
                    label={t('users.fields.passportIssuedAt')}
                    required
                    type="date"
                    value={passportIssuedAt}
                    onChange={(e) => setPassportIssuedAt(e.target.value)}
                  />
                  <label className="flex flex-col gap-1">
                    <span className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
                      <span
                        aria-hidden
                        title={t('users.requiredField')}
                        className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]"
                      />
                      <span>{t('users.fields.passportMainScan')}</span>
                    </span>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) =>
                        setPassportMainFile(e.target.files?.[0] ?? null)
                      }
                      className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
                      <span
                        aria-hidden
                        title={t('users.requiredField')}
                        className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]"
                      />
                      <span>{t('users.fields.passportRegistrationScan')}</span>
                    </span>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) =>
                        setPassportRegistrationFile(e.target.files?.[0] ?? null)
                      }
                      className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
                    />
                  </label>
                </div>
              ) : null}

              {role === 'manager' ? (
                <div className="space-y-3">
                  <Input
                    label={t('users.fields.poaNumber')}
                    required
                    value={poaNumber}
                    onChange={(e) => setPoaNumber(e.target.value)}
                  />
                  <Input
                    label={t('users.fields.poaExpiresAt')}
                    required
                    type="date"
                    value={poaExpiresAt}
                    onChange={(e) => setPoaExpiresAt(e.target.value)}
                  />
                  <label className="flex flex-col gap-1">
                    <span className="stryzh-input-label !inline-flex items-center gap-3 leading-none">
                      <span
                        aria-hidden
                        title={t('users.requiredField')}
                        className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--stryzh-color-accent-orange)]"
                      />
                      <span>{t('users.fields.poaScan')}</span>
                    </span>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setPoaFile(e.target.files?.[0] ?? null)}
                      className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
                    />
                  </label>
                </div>
              ) : null}
            </div>
          </div>
          <div className="border-t border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
            {error ? (
              <p className="mb-3 text-sm text-[color:var(--stryzh-color-semantic-error)]">{error}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={() => void submit()} disabled={saving}>
                {saving ? t('users.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </aside>
      </div>
      {pwDialog ? (
        <PasswordDialog
          password={pwDialog}
          onClose={() => {
            setPwDialog(null)
            onClose()
            if (createdUserId) navigate(`/users/${createdUserId}`)
            setCreatedUserId(null)
          }}
        />
      ) : null}
    </>
  )
}
